import React, { ReactNode } from "react";
import { Box, Divider, Modal, SxProps, Theme } from "@mui/material";
import styled from "@emotion/styled";
import CloseIcon from "@/assets/icons/CloseIcon";
import { useIsMobile } from "@/common/hooks";

interface CustomModalProps {
  zindex?: number;
  overflowY?: string;
  childrenHeight?: string;
  open: boolean;
  onClose: () => void;
  title?: string;
  height?: string;
  children: ReactNode;
  width?: string;
  minHeight?: string;
  top?: string;
  left?: string;
  maxHeight?: string;
  padding?: string;
  /** 창 밖(body 에 띄운 달력 시트·팝업 등)으로 포커스가 나가도 되게 — 포커스 가두기를 끈다 */
  disableEnforceFocus?: boolean;
  /** 몸통 스크롤 잠금을 끈다(sticky 머리·입력줄이 있는 화면 — ConfirmModal 같은 옵션 설명). 기본은 잠금 */
  disableScrollLock?: boolean;
}

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 16px 20px;
`;

const ModalTitle = styled.div`
  font-size: 17px;
  font-weight: bold;
  text-align: center;
`;

const CloseButton = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
`;

const ModalContent = styled.div<{ padding?: string }>`
  padding: ${props => props.padding || '20px'};
`;

const CustomModal = ({
  zindex,
  overflowY,
  childrenHeight,
  open,
  onClose,
  title,
  height,
  children,
  width,
  minHeight,
  top,
  left,
  maxHeight,
  padding,
  disableEnforceFocus,
  disableScrollLock = false,
}: CustomModalProps) => {
  const isMobile = useIsMobile();
  const defaultWidth = isMobile ? 'calc(100vw - 2rem)' : '400px';

  // 외부에서 top/left를 명시적으로 지정한 경우만 absolute 모드 사용 (legacy 호환).
  // 그 외엔 MUI Modal에 flexbox 센터링을 걸어 한 번에 가운데 배치.
  const useAbsolute = top !== undefined || left !== undefined;

  const boxStyle = useAbsolute
    ? {
        position: "absolute" as const,
        top: top ?? "50%",
        left: left ?? "50%",
        transform: "translate(-50%, -50%)",
        width: width ?? defaultWidth,
        height: height ?? "260px",
        maxHeight: maxHeight ?? "calc(100vh - 2rem)",
        borderRadius: 5,
        overflowY: overflowY ?? "auto",
        overflowX: "hidden",
        msOverflowStyle: "none",
        scrollbarWidth: "none",
      }
    : {
        width: width ?? defaultWidth,
        height: height ?? "260px",
        maxHeight: maxHeight ?? "calc(100vh - 2rem)",
        borderRadius: 5,
        overflowY: overflowY ?? "auto",
        overflowX: "hidden",
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        position: "relative" as const,
      };

  return (
    <Modal
      sx={{
        zIndex: zindex,
        border: "none",
        wordWrap: "break-word",
        minHeight: minHeight ? minHeight : "",
        outline: "none",
        ...(useAbsolute
          ? {}
          : {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }),
        "&:focus, &:focus-visible": {
          outline: "none",
        }
      }}
      open={open}
      onClose={onClose}
      disableEnforceFocus={disableEnforceFocus}
      disableScrollLock={disableScrollLock}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{
        ...boxStyle,
        backgroundColor: "#fff",
        outline: "none",
        border: "none",
        "&:focus, &:focus-visible": {
          outline: "none",
          border: "none",
        }
      } as SxProps<Theme>}>
        {title && (
          <>
            <ModalHeader>
              <ModalTitle>{title}</ModalTitle>
              <CloseButton onClick={onClose}>
                <CloseIcon onClick={onClose} />
              </CloseButton>
            </ModalHeader>
            <Divider />
          </>
        )}
        <ModalContent padding={padding}>
          {children}
        </ModalContent>
      </Box>
    </Modal>
  );
};

export default CustomModal
